/**
 * Chiamate alle API dello spazio dei volumi di un sito ospitato (ADR 0046).
 *
 * Ogni piano include uno spazio per progetto ospitato (3 GiB Free, 10 GiB Base, 50 GiB
 * Premium). L'utente può sceglierne di più: lo spazio oltre l'incluso costa 20 crediti
 * per GiB al mese, addebitati ogni giorno dal saldo dei crediti.
 *
 * Provvisorio dichiarato: percorsi e tipi scritti a mano (CLAUDE.md §2.3), presidiati da
 * `storage.test.ts`.
 */

import type { ApiClient } from '../client';

/** Stato dello spazio: incluso, con spazio in più, o crediti finiti. */
export type StorageState = 'included' | 'extra' | 'storage_unpaid';

/** Codici dei rifiuti della scelta, tradotti dalla pagina. */
export const STORAGE_REFUSAL_CODES = [
  'storage_below_included',
  'storage_below_allocated',
  'storage_too_large',
  'insufficient_credits',
] as const;

/** Codice del 409 quando il saldo non copre lo spazio in più fino a fine mese. */
export const INSUFFICIENT_CREDITS = 'insufficient_credits';

/** Spazio dei volumi di un sito. */
export interface HostingStorage {
  /** GiB inclusi nel piano. */
  included_gib: number;
  /** GiB scelti: il limite dello spazio. */
  chosen_gib: number;
  /** GiB già chiesti dai volumi; `null` se non noti. */
  allocated_gib: number | null;
  /** GiB oltre l'incluso. */
  extra_gib: number;
  /** Costo dello spazio in più, crediti al mese. */
  monthly_credits: number;
  /** Saldo dei crediti; `null` se non leggibile. */
  credits_balance: number | null;
  /** Spazio massimo sceglibile. */
  max_gib: number;
  /** Crediti per GiB in più al mese. */
  credits_per_gib_month: number;
  /** Crediti già addebitati nel mese. */
  charged_this_month: number;
  /** Stato. */
  state: StorageState;
}

/**
 * Percorso della rotta dello spazio.
 *
 * @param setId Set.
 * @param projectId Progetto.
 * @returns Il percorso.
 */
function storagePath(setId: string, projectId: string): string {
  return `/api/v1/agent-sets/${encodeURIComponent(setId)}/projects/${encodeURIComponent(projectId)}/hosting/storage`;
}

/**
 * Costo mensile di uno spazio scelto.
 *
 * @param storage Stato attuale, per incluso e prezzo.
 * @param gib GiB scelti.
 * @returns Crediti al mese; zero dentro l'incluso.
 */
export function monthlyCostOf(storage: HostingStorage, gib: number): number {
  return Math.max(gib - storage.included_gib, 0) * storage.credits_per_gib_month;
}

/** Operazioni sullo spazio dei siti, esposte alla UI. */
export class HostingStorageApi {
  /**
   * @param client Client HTTP verso il gateway.
   */
  constructor(private readonly client: ApiClient) {}

  /**
   * Legge lo spazio del sito di un progetto del set.
   *
   * @param setId Set.
   * @param projectId Progetto.
   * @returns Lo spazio.
   * @throws ApiError 404 se il set non contiene il progetto o il sito non c'è.
   */
  get(setId: string, projectId: string): Promise<HostingStorage> {
    return this.client.request<HostingStorage>('GET', storagePath(setId, projectId));
  }

  /**
   * Cambia lo spazio scelto.
   *
   * @param setId Set.
   * @param projectId Progetto.
   * @param gib GiB interi; `null` torna all'incluso.
   * @returns Lo spazio dopo il cambio.
   * @throws ApiError 422 `storage_below_included` o `storage_too_large`, 409
   *   `storage_below_allocated` o `insufficient_credits`.
   */
  choose(setId: string, projectId: string, gib: number | null): Promise<HostingStorage> {
    return this.client.request<HostingStorage>('PUT', storagePath(setId, projectId), { chosen_gib: gib });
  }
}
